import { useState } from 'react';
import { Alert, Button, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CameraOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { AppFeedbackCategory, type AppFeedbackAdminItemDto } from 'breuer-supercharged-sdk';
import { useAppFeedbackList, useTenants } from '@/hooks/useSystemAdminApi';
import AppFeedbackDetailDrawer from './AppFeedbackDetailDrawer';
import { AppFeedbackDeliveryStatus } from './AppFeedbackDeliveryStatus';
import {
    feedbackCategoryMeta,
    countUndeliveredFeedback,
    formatFeedbackCreatedAt,
    isSafeHttpUrl,
    pageUrlLabel,
    truncateText,
} from './app_feedback_utils';

const { Title, Text } = Typography;

const DEFAULT_PAGE_SIZE = 20;

const MESSAGE_CLAMP_STYLE = {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
} as const;

/**
 * Admin → Feedback: every in-app feedback of all tenants, newest first.
 *
 * Reads the `app_feedback` table directly (via `GET /system-admin/app-feedback`),
 * so reports are visible here even when the notification mail to the support
 * inbox fails — which it did in prod for three months without anyone noticing.
 */
export default function AppFeedbackOverview() {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [tenantId, setTenantId] = useState<string | undefined>(undefined);
    const [category, setCategory] = useState<AppFeedbackCategory | undefined>(undefined);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const { data, isLoading, isFetching, isError, refetch } = useAppFeedbackList({
        page,
        pageSize,
        tenantId,
        category,
    });
    const { data: tenants } = useTenants();

    const items = data?.items ?? [];
    const notSentOnPage = countUndeliveredFeedback(items);

    const columns: ColumnsType<AppFeedbackAdminItemDto> = [
        {
            title: 'Datum',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (createdAt: string) => formatFeedbackCreatedAt(createdAt),
        },
        {
            title: 'Mandant',
            key: 'tenant',
            width: 170,
            render: (_, record) => record.tenantName ?? <Text type="secondary">{record.tenantId}</Text>,
        },
        {
            title: 'Kategorie',
            dataIndex: 'category',
            key: 'category',
            width: 100,
            render: (value: string) => {
                const meta = feedbackCategoryMeta(value);
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
        {
            title: 'Nachricht',
            dataIndex: 'message',
            key: 'message',
            width: 380,
            render: (message: string, record) => (
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', minWidth: 0 }}>
                    {record.hasScreenshot && (
                        <Tooltip title="Mit Screenshot">
                            <CameraOutlined style={{ marginTop: 4, flexShrink: 0 }} />
                        </Tooltip>
                    )}
                    <Tooltip title={truncateText(message, 600)}>
                        <span style={MESSAGE_CLAMP_STYLE}>{truncateText(message, 240)}</span>
                    </Tooltip>
                </div>
            ),
        },
        {
            title: 'Seite',
            dataIndex: 'pageUrl',
            key: 'pageUrl',
            width: 200,
            render: (pageUrl: string | null) => {
                if (!pageUrl) return <Text type="secondary">—</Text>;
                const label = truncateText(pageUrlLabel(pageUrl), 40);
                return isSafeHttpUrl(pageUrl) ? (
                    <a
                        href={pageUrl}
                        target="_blank"
                        rel="noreferrer"
                        title={pageUrl}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {label}
                    </a>
                ) : (
                    <span title={pageUrl}>{label}</span>
                );
            },
        },
        {
            title: 'Melder',
            key: 'reporter',
            width: 200,
            render: (_, record) => record.createdByEmail ?? record.createdByUserId ?? '—',
        },
        {
            title: 'Mail-Status',
            dataIndex: 'emailSentAt',
            key: 'emailSentAt',
            width: 200,
            render: (emailSentAt: string | null, record) => (
                <AppFeedbackDeliveryStatus
                    reviewStatus={record.reviewStatus}
                    emailSentAt={emailSentAt}
                    systemReleaseReason={record.systemReleaseReason}
                />
            ),
        },
    ];

    return (
        <div>
            <div
                style={{
                    marginBottom: 24,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 16,
                    flexWrap: 'wrap',
                }}
            >
                <div>
                    <Title level={2}>Feedback</Title>
                    <Text type="secondary">
                        Alle Meldungen aus dem Feedback-Button der App, neueste zuerst
                    </Text>
                </div>
                <Space wrap>
                    <Select
                        allowClear
                        placeholder="Alle Mandanten"
                        style={{ width: 220 }}
                        value={tenantId}
                        onChange={(value?: string) => {
                            setTenantId(value);
                            setPage(1);
                        }}
                        options={(tenants ?? []).map((t) => ({ value: t.id, label: t.name }))}
                        showSearch={{ optionFilterProp: 'label' }}
                    />
                    <Select
                        allowClear
                        placeholder="Alle Kategorien"
                        style={{ width: 170 }}
                        value={category}
                        onChange={(value?: AppFeedbackCategory) => {
                            setCategory(value);
                            setPage(1);
                        }}
                        // Bind to the enum VALUES (wire format), never the keys.
                        options={Object.values(AppFeedbackCategory).map((value) => ({
                            value,
                            label: feedbackCategoryMeta(value).label,
                        }))}
                    />
                    <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
                        Aktualisieren
                    </Button>
                </Space>
            </div>

            {isError && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    title="Feedback konnte nicht geladen werden."
                />
            )}
            {notSentOnPage > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    title={`${notSentOnPage} ${notSentOnPage === 1 ? 'Meldung' : 'Meldungen'} auf dieser Seite ohne Benachrichtigungs-Mail an den Support`}
                    description="Diese Meldungen haben das Support-Postfach nicht erreicht – bitte hier sichten. Seit 12.10.2026 erzeugt jede neue Meldung zusätzlich ein Sentry-Event (subsystem:app-feedback); Fehler beim Mailversand stehen dort unter subsystem:outbound-mail."
                />
            )}

            <Table<AppFeedbackAdminItemDto>
                rowKey="id"
                columns={columns}
                dataSource={items}
                loading={isLoading}
                size="middle"
                scroll={{ x: 1100 }}
                onRow={(record) => ({
                    onClick: () => setSelectedId(record.id),
                    style: { cursor: 'pointer' },
                })}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.total ?? 0,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total) => `${total} Meldungen`,
                    onChange: (nextPage, nextPageSize) => {
                        setPage(nextPageSize !== pageSize ? 1 : nextPage);
                        setPageSize(nextPageSize);
                    },
                }}
            />

            <AppFeedbackDetailDrawer feedbackId={selectedId} onClose={() => setSelectedId(null)} />
        </div>
    );
}
